import type {
  CreateIngredientPayload,
  CreateLotPayload,
  IngredientListRow,
  InventoryLotListRow,
  MovementListRow,
  RecipeWithVariantResult,
  UpsertRecipePayload,
} from "@/modules/inventory/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export const inventoryIngredientsApi = {
  async list(): Promise<IngredientListRow[]> {
    return fetcher.get<IngredientListRow[]>(
      `${config.apiUrl}/inventory/ingredients`,
    );
  },

  async get(id: string): Promise<IngredientListRow> {
    return fetcher.get<IngredientListRow>(
      `${config.apiUrl}/inventory/ingredients/${id}`,
    );
  },

  async lookup(params: {
    search?: string;
    page: number;
    limit?: number;
    categoryId?: string;
  }): Promise<{ items: IngredientListRow[]; nextPage: number | null }> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("page", String(params.page));
    if (params.limit) searchParams.set("limit", String(params.limit));
    if (params.categoryId) searchParams.set("categoryId", params.categoryId);
    return fetcher.get<{ items: IngredientListRow[]; nextPage: number | null }>(
      `${config.apiUrl}/inventory/ingredients/lookup?${searchParams.toString()}`,
    );
  },

  async create(payload: CreateIngredientPayload): Promise<IngredientListRow> {
    return fetcher.post<IngredientListRow>(
      `${config.apiUrl}/inventory/ingredients`,
      payload,
    );
  },

  async update(
    id: string,
    payload: CreateIngredientPayload,
  ): Promise<IngredientListRow> {
    return fetcher.put<IngredientListRow>(
      `${config.apiUrl}/inventory/ingredients/${id}`,
      payload,
    );
  },

  async remove(id: string): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/inventory/ingredients/${id}`,
    );
  },

  async bulkAssignCategory(payload: {
    ingredientIds: string[];
    categoryId: string | null;
  }): Promise<{ updated: number }> {
    return fetcher.post<{ updated: number }>(
      `${config.apiUrl}/inventory/ingredients/bulk-category`,
      payload,
    );
  },
};

export const BASE_UNITS = ["g", "kg", "ml", "l", "pcs"] as const;
export type BaseUnit = (typeof BASE_UNITS)[number];

export type InventoryLotsListParams = {
  branchId?: string;
  itemType?: "INGREDIENT" | "RETAIL_VARIANT";
  itemId?: string;
  ingredientCategoryId?: string;
  sort?: "fefo" | "fifo";
  sortField?: string;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type InventoryLotsListResult = {
  items: InventoryLotListRow[];
  total: number;
};

export type InventoryLotsSummary = {
  expiredCount: number;
  nearExpiryCount: number;
  healthyCount: number;
};

export type InventoryLotsSummaryParams = {
  branchId?: string;
  itemType?: "INGREDIENT" | "RETAIL_VARIANT";
  itemId?: string;
  ingredientCategoryId?: string;
};

export type InventoryLotsLookupParams = {
  branchId?: string;
  search?: string;
  page: number;
  limit?: number;
};

export type InventoryLotsLookupResult = {
  items: InventoryLotListRow[];
  nextPage: number | null;
};

export const inventoryLotsApi = {
  async lookup(
    params: InventoryLotsLookupParams,
  ): Promise<InventoryLotsLookupResult> {
    const limit = params.limit ?? 20;
    const page = Math.max(1, params.page);
    const skip = (page - 1) * limit;
    const searchParams = new URLSearchParams();
    if (params.branchId) searchParams.set("branchId", params.branchId);
    if (params.search?.trim()) searchParams.set("q", params.search.trim());
    searchParams.set("limit", String(limit));
    searchParams.set("skip", String(skip));
    const data = await fetcher.get<{
      items: InventoryLotListRow[];
      total: number;
    }>(`${config.apiUrl}/inventory/lots/lookup?${searchParams.toString()}`);
    const items = Array.isArray(data.items) ? data.items : [];
    const total = typeof data.total === "number" ? data.total : items.length;
    const nextPage = skip + limit < total ? page + 1 : null;
    return { items, nextPage };
  },

  async getForLookup(id: string): Promise<InventoryLotListRow | null> {
    if (!id) return null;
    const data = await fetcher.get<{ item: InventoryLotListRow | null }>(
      `${config.apiUrl}/inventory/lots/lookup/${encodeURIComponent(id)}`,
    );
    return data.item ?? null;
  },

  async list(
    params: InventoryLotsListParams = {},
  ): Promise<InventoryLotsListResult> {
    const searchParams = new URLSearchParams();
    if (params.branchId) searchParams.set("branchId", params.branchId);
    if (params.itemType) searchParams.set("itemType", params.itemType);
    if (params.itemId) searchParams.set("itemId", params.itemId);
    if (params.ingredientCategoryId) {
      searchParams.set("ingredientCategoryId", params.ingredientCategoryId);
    }
    if (params.sort) searchParams.set("sort", params.sort);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null)
      searchParams.set("offset", String(params.offset));
    return fetcher.get<InventoryLotsListResult>(
      `${config.apiUrl}/inventory/lots?${searchParams.toString()}`,
    );
  },

  async summary(
    params: InventoryLotsSummaryParams = {},
  ): Promise<InventoryLotsSummary> {
    const searchParams = new URLSearchParams();
    if (params.branchId) searchParams.set("branchId", params.branchId);
    if (params.itemType) searchParams.set("itemType", params.itemType);
    if (params.itemId) searchParams.set("itemId", params.itemId);
    if (params.ingredientCategoryId) {
      searchParams.set("ingredientCategoryId", params.ingredientCategoryId);
    }
    return fetcher.get<InventoryLotsSummary>(
      `${config.apiUrl}/inventory/lots/summary?${searchParams.toString()}`,
    );
  },

  async createLot(payload: CreateLotPayload): Promise<{
    id: string;
    branchId: string;
    itemType: "INGREDIENT" | "RETAIL_VARIANT";
    itemId: string;
    lotNo: string;
    expiryDate: string | null;
    receivedAt: Date;
    qtyInBaseUnit: number;
    qtyRemaining: number;
    costPerUnitLak: number | null;
  }> {
    return fetcher.post<{
      id: string;
      branchId: string;
      itemType: "INGREDIENT" | "RETAIL_VARIANT";
      itemId: string;
      lotNo: string;
      expiryDate: string | null;
      receivedAt: Date;
      qtyInBaseUnit: number;
      qtyRemaining: number;
      costPerUnitLak: number | null;
    }>(`${config.apiUrl}/inventory/lots`, payload);
  },
};

export type InventoryMovementsListParams = {
  branchId?: string;
  lotId?: string;
  reason?: MovementListRow["reason"];
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type InventoryMovementsListResult = {
  items: MovementListRow[];
  total: number;
};

export type InventoryMovementsSummaryParams = {
  branchId?: string;
};

export type InventoryMovementsSummary = {
  inflowToday: number;
  outflowToday: number;
  netAdjustmentToday: number;
};

export const inventoryMovementsApi = {
  async summary(
    params: InventoryMovementsSummaryParams = {},
  ): Promise<InventoryMovementsSummary> {
    const searchParams = new URLSearchParams();
    if (params.branchId) searchParams.set("branchId", params.branchId);
    return fetcher.get<InventoryMovementsSummary>(
      `${config.apiUrl}/inventory/movements/summary?${searchParams.toString()}`,
    );
  },

  async list(
    params: InventoryMovementsListParams = {},
  ): Promise<InventoryMovementsListResult> {
    const searchParams = new URLSearchParams();
    if (params.branchId) searchParams.set("branchId", params.branchId);
    if (params.lotId) searchParams.set("lotId", params.lotId);
    if (params.reason) searchParams.set("reason", params.reason);
    if (params.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params.dateTo) searchParams.set("dateTo", params.dateTo);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null)
      searchParams.set("offset", String(params.offset));
    return fetcher.get<InventoryMovementsListResult>(
      `${config.apiUrl}/inventory/movements?${searchParams.toString()}`,
    );
  },

  async createAdjustment(payload: {
    lotId: string;
    qtyDelta: number;
  }): Promise<{
    id: string;
    branchId: string;
    lotId: string;
    reason: "ADJUST";
    qtyDelta: number;
    createdAt: string;
  }> {
    return fetcher.post(`${config.apiUrl}/inventory/movements/adjust`, payload);
  },
};

export type StockTransferStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "REJECTED"
  | "APPROVED"
  | "SHIPPED"
  | "RECEIVED"
  | "CLOSED"
  | "CANCELLED";

export type StockTransferListRow = {
  id: string;
  requestNo: string;
  fromBranchId: string;
  fromBranchCode: string;
  fromBranchName: string;
  toBranchId: string;
  toBranchCode: string;
  toBranchName: string;
  status: StockTransferStatus;
  note: string | null;
  submittedAt: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lineCount: number;
};

export type StockTransferLineDetail = {
  id: string;
  lineNo: number;
  itemType: "INGREDIENT" | "RETAIL_VARIANT";
  itemId: string;
  itemName: string;
  qtyRequested: number;
  qtyApproved: number | null;
  qtyShipped: number | null;
  qtyReceived: number | null;
  unit: string;
  toBaseFactor: number;
};

export type StockTransferDetail = {
  id: string;
  requestNo: string;
  fromBranchId: string;
  fromBranchCode: string;
  fromBranchName: string;
  toBranchId: string;
  toBranchCode: string;
  toBranchName: string;
  status: StockTransferStatus;
  requestedByUserId: string;
  submittedAt: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  closedAt: string | null;
  note: string | null;
  internalNote: string | null;
  rejectReason: string | null;
  createdAt: string;
  updatedAt: string;
  lines: StockTransferLineDetail[];
  shipments: Array<{
    id: string;
    lineId: string;
    sourceLotId: string;
    sourceLotNo: string;
    qty: number;
    movementOutId: string;
    movementInId: string | null;
    destLotId: string | null;
    costPerUnitLak: number | null;
  }>;
};

export type StockTransfersListParams = {
  status?: StockTransferStatus;
  fromBranchId?: string;
  toBranchId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  limit?: number;
  offset?: number;
};

export const stockTransfersApi = {
  async list(
    params: StockTransfersListParams = {},
  ): Promise<{ items: StockTransferListRow[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set("status", params.status);
    if (params.fromBranchId)
      searchParams.set("fromBranchId", params.fromBranchId);
    if (params.toBranchId) searchParams.set("toBranchId", params.toBranchId);
    if (params.submittedFrom)
      searchParams.set("submittedFrom", params.submittedFrom);
    if (params.submittedTo) searchParams.set("submittedTo", params.submittedTo);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null)
      searchParams.set("offset", String(params.offset));
    return fetcher.get(
      `${config.apiUrl}/inventory/transfers?${searchParams.toString()}`,
    );
  },

  async get(id: string): Promise<StockTransferDetail> {
    const res = await fetcher.get<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}`,
    );
    return res.item;
  },

  async hubAvailability(params: {
    itemType: "INGREDIENT" | "RETAIL_VARIANT";
    itemId: string;
  }): Promise<{ hubBranchId: string; qtyAvailable: number }> {
    const searchParams = new URLSearchParams({
      itemType: params.itemType,
      itemId: params.itemId,
    });
    return fetcher.get(
      `${config.apiUrl}/inventory/transfers/hub-availability?${searchParams.toString()}`,
    );
  },

  async approve(
    id: string,
    body: {
      lines?: Array<{ lineId: string; qtyApproved: number }>;
      internalNote?: string | null;
    },
  ): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/approve`,
      body,
    );
    return res.item;
  },

  async reject(
    id: string,
    body: { rejectReason?: string | null },
  ): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/reject`,
      body,
    );
    return res.item;
  },

  async reviseApproved(
    id: string,
    body: {
      lines: Array<{ lineId: string; qtyApproved: number }>;
      internalNote?: string | null;
    },
  ): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/revise-approved`,
      body,
    );
    return res.item;
  },

  async cancel(id: string): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/cancel`,
      {},
    );
    return res.item;
  },

  async ship(
    id: string,
    body: {
      picks?: Array<{
        lineId: string;
        allocations: Array<{ lotId: string; qty: number }>;
      }>;
    },
  ): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/ship`,
      body,
    );
    return res.item;
  },

  async close(id: string): Promise<StockTransferDetail> {
    const res = await fetcher.post<{ item: StockTransferDetail }>(
      `${config.apiUrl}/inventory/transfers/${id}/close`,
      {},
    );
    return res.item;
  },
};

export const inventoryRecipesApi = {
  async getByVariantId(variantId: string): Promise<RecipeWithVariantResult> {
    return fetcher.get<RecipeWithVariantResult>(
      `${config.apiUrl}/inventory/variants/${variantId}/recipe`,
    );
  },

  async upsert(
    variantId: string,
    payload: UpsertRecipePayload,
  ): Promise<RecipeWithVariantResult> {
    return fetcher.put<RecipeWithVariantResult>(
      `${config.apiUrl}/inventory/variants/${variantId}/recipe`,
      payload,
    );
  },
};
